// filepath: c:\Users\greg\regulator\express\analysis-app\src\components\dashboard\charts\TimeSeriesChart.jsx
import { useMemo } from 'react'
import BaseChart from './BaseChart'
import PropTypes from 'prop-types'

function TimeSeriesChart({ 
  data = [], 
  title = 'Time Series', 
  height = 350,
  color = '#007bff',
  showTooltip = true,
  showGrid = true,
  curve = 'smooth',
  loading = false,
  error = null,
  ...props 
}) {
  // Process time series data
  const processedData = useMemo(() => {
    if (!Array.isArray(data)) return []
    
    return data.map(point => {
      if (typeof point === 'object' && point.x !== undefined && point.y !== undefined) {
        return {
          x: new Date(point.x).getTime(),
          y: point.y,
        }
      }
      return point
    })
  }, [data])

  // Time series specific options
  const timeSeriesOptions = {
    chart: {
      type: 'line',
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: true,
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
    },
    title: {
      text: title,
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333',
      },
    },
    stroke: {
      curve,
      width: 2,
    },
    colors: [color],
    grid: {
      show: showGrid,
      borderColor: '#e9ecef',
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      type: 'datetime',      labels: {
        style: {
          colors: '#8e8da4',
        },
        formatter(value) {
          return new Date(value).toLocaleTimeString()
        },
      },
      axisBorder: {
        show: true,
        color: '#e9ecef',
      },
      axisTicks: {
        show: true,
        color: '#e9ecef',
      },
    },
    yaxis: {      labels: {
        style: {
          colors: '#8e8da4',
        },
        formatter(value) {
          return value?.toFixed(2) || '0'
        },
      },
    },
    tooltip: {
      enabled: showTooltip,
      theme: 'light',
      x: {
        format: 'dd/MM/yy HH:mm:ss',
      },      y: {
        formatter(value) {
          return value?.toFixed(2) || '0'
        },
      },
    },
    legend: {
      show: false,
    },
    markers: {
      size: 0,
      hover: {
        size: 5,
      },
    },
  }

  // Prepare series data
  const series = [{
    name: title,
    data: processedData,
  }]

  return (
    <BaseChart
      series={series}
      options={timeSeriesOptions}
      type="line"
      height={height}
      loading={loading}
      error={error}
      fallbackText="No time series data available"
      {...props}
    />
  )
}

TimeSeriesChart.propTypes = {
  data: PropTypes.array,
  title: PropTypes.string,
  height: PropTypes.number,
  color: PropTypes.string,
  showTooltip: PropTypes.bool,
  showGrid: PropTypes.bool,
  curve: PropTypes.oneOf(['smooth', 'straight', 'stepline']),
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
}

export default TimeSeriesChart
